import { User, IdTokenClaims } from "oidc-client-ts";
import {
  AddOnEntitlements,
  MemberGroups,
  PremiumEntitlements,
} from "shared-types";
import { jwtDecode } from "jwt-decode";
import { authAxios as axios } from "./authAxios";
import config from "../../config";
import { SL_MEMBER_GROUP, UserAttributes } from "./constants";
import { checkIsAgent } from "../../utils/checkIsAgent";

enum AccountType {
  DefaultUser = 0,
  CastingDirector = 1,
  Performer = 2,
  Agent = 3,
  CastingSearchOnly = 4,
}

export async function getWhoAmI() {
  const { data } = await axios(config.whoAmIUrl);

  return {
    ...data,
    type: AccountType[data.role],
  };
}

// this function allow to access user outside the context
export function getUser() {
  const oidcStorage = localStorage.getItem(
    `oidc.user:${config.oidc?.authority}:${config.oidc?.clientId}`
  );

  if (!oidcStorage) {
    return null;
  }

  return User.fromStorageString(oidcStorage);
}

export function getJwtClaims(): IdTokenClaims | null {
  const user = getUser();

  if (!user) {
    return null;
  }

  return jwtDecode(user.access_token);
}

export function getUserGroup(): MemberGroups {
  const user = getUser();

  return (
    (user?.profile?.[SL_MEMBER_GROUP] as MemberGroups) ||
    MemberGroups.defaultUser
  );
}

export function doesUserHaveEntitlements(
  feature: PremiumEntitlements | AddOnEntitlements
): boolean {
  const jwtClaims = getJwtClaims();
  const isAgent = checkIsAgent();

  if (!jwtClaims) return false;

  if (isAgent) return true;

  const userEntitlements = jwtClaims["entitlements"] || "[]";
  const parsedEntitlements = JSON.parse(userEntitlements as string);

  if (parsedEntitlements.length === 0) {
    return false;
  }

  return parsedEntitlements.some(
    (entitlement: string) => entitlement === feature
  );
}

export function getUserAttribute<T extends string>(
  attr: UserAttributes,
  defaultValue?: T
): T {
  const user = getUser();
  const jwtClaims = getJwtClaims();

  if (!user || !jwtClaims) {
    return defaultValue as T;
  }

  return (user.profile[attr] || jwtClaims[attr]) as T;
}

export function getProfileCheckerAuthQueryParams():
  | Record<string, string>
  | undefined {
  const params = new URLSearchParams(window.location.search);

  const provider = params.get("provider");

  if (provider) return { acr_values: `idp:${provider}` };
}
